/**
 * GlobalView — Globe Store
 *
 * Zustand store for 3D globe state (§11.4, Phase 2).
 * Manages viewer reference, camera position, selection,
 * and view mode.
 */

import { create } from 'zustand';
import * as Cesium from 'cesium';
import type { BaseLayerStyle } from '../services/baseLayer';

// ─── Types ──────────────────────────────────────────────────────

export type ViewMode = 'globe' | 'region' | 'city';
export type TileMode = 'google3d' | 'cesium';
export type VisualMode = 'standard' | 'crt' | 'nvg' | 'flir';

// ─── Filter Settings (tunable shader uniforms) ─────────────────

export interface CrtSettings {
  scanlineIntensity: number;  // 0.0–0.20, default 0.08
  scanlineFrequency: number;  // 200–2000, default 800 (lines)
  distortion: number;         // 0.0–0.40, default 0.15
  greenTint: number;          // 0.0–1.0,  default 0.30
  bloom: number;              // 0.8–1.5,  default 1.15
  vignette: number;           // 0.0–4.0,  default 2.0
}

export interface NvgSettings {
  brightness: number;         // 0.5–2.5,  default 1.4
  noiseIntensity: number;     // 0.0–0.30, default 0.12
  vignetteSize: number;       // 0.3–1.5,  default 0.6
  greenAmount: number;        // 0.5–1.0,  default 1.0
  scanlines: number;          // 0.0–0.10, default 0.03
}

export interface FlirSettings {
  contrast: number;           // 1.0–2.5,  default 1.4
  vignette: number;           // 0.0–3.0,  default 1.2
}

export interface FilterSettings {
  crt: CrtSettings;
  nvg: NvgSettings;
  flir: FlirSettings;
}

export const DEFAULT_CRT: CrtSettings = {
  scanlineIntensity: 0.08,
  scanlineFrequency: 800,
  distortion: 0.15,
  greenTint: 0.30,
  bloom: 1.15,
  vignette: 2.0,
};

export const DEFAULT_NVG: NvgSettings = {
  brightness: 1.4,
  noiseIntensity: 0.12,
  vignetteSize: 0.6,
  greenAmount: 1.0,
  scanlines: 0.03,
};

export const DEFAULT_FLIR: FlirSettings = {
  contrast: 1.4,
  vignette: 1.2,
};

export const DEFAULT_FILTER_SETTINGS: FilterSettings = {
  crt: { ...DEFAULT_CRT },
  nvg: { ...DEFAULT_NVG },
  flir: { ...DEFAULT_FLIR },
};

export interface CameraPosition {
  lat: number;
  lon: number;
  /** Altitude in meters above WGS84 ellipsoid */
  alt: number;
  heading: number;
  pitch: number;
}

interface GlobeState {
  /** CesiumJS Viewer instance */
  viewer: Cesium.Viewer | null;

  /** Current camera position */
  cameraPosition: CameraPosition;

  /** Currently selected entity ID */
  selectedEntityId: string | null;

  /** Entity ID the camera is tracking (following) */
  trackedEntityId: string | null;

  /** Current view mode based on camera altitude */
  viewMode: ViewMode;

  /** Mouse cursor geo-position */
  cursorPosition: { lat: number; lon: number } | null;

  /** Whether the globe is ready (tiles loaded, viewer initialized) */
  ready: boolean;

  /** Active tile mode (google3d = photorealistic, cesium = OSM buildings) */
  tileMode: TileMode;

  /** Active base layer style (only visible in cesium mode) */
  baseLayerStyle: BaseLayerStyle;

  /** Whether the air layer (aircraft) is visible */
  airLayerVisible: boolean;

  /** Whether aircraft labels (callsigns) are visible */
  airLabelsVisible: boolean;

  /** Whether the rail layer (trains) is visible */
  railLayerVisible: boolean;

  /** Whether train labels are visible */
  railLabelsVisible: boolean;

  /** Whether the space layer (satellites) is visible */
  spaceLayerVisible: boolean;

  /** Whether satellite labels are visible */
  spaceLabelsVisible: boolean;

  /** Whether Starlink constellation is visible (sub-toggle of space) */
  starlinkVisible: boolean;

  /** Whether the EW/Signal layer (GPS jamming heatmap) is visible */
  ewLayerVisible: boolean;

  /** Whether the zones layer is visible */
  zonesLayerVisible: boolean;

  /** Whether the sea/maritime layer (vessels) is visible */
  seaLayerVisible: boolean;

  /** Whether vessel labels are visible */
  seaLabelsVisible: boolean;

  /** Whether the earthquake layer is visible */
  quakeLayerVisible: boolean;

  /** Whether global earthquakes (USGS) sub-layer is visible */
  quakeGlobalVisible: boolean;

  /** Whether Swiss earthquakes (SED) sub-layer is visible */
  quakeSwissVisible: boolean;

  /** Whether the wildfire layer is visible */
  fireLayerVisible: boolean;

  /** Whether the EONET natural events layer is visible */
  eonetLayerVisible: boolean;

  /** Active visual mode / shader preset (§14.2, Phase 6) */
  visualMode: VisualMode;

  /** Tunable filter settings for CRT, NVG, FLIR shaders */
  filterSettings: FilterSettings;

  // ─── Actions ──────────────────────────────────────────────────

  /** Register the CesiumJS Viewer instance */
  setViewer: (viewer: Cesium.Viewer | null) => void;

  /** Update camera position (called on camera move) */
  updateCameraPosition: (pos: CameraPosition) => void;

  /** Select an entity by ID */
  setSelectedEntity: (id: string | null) => void;

  /** Set tracked entity (camera follows this entity) */
  setTrackedEntity: (id: string | null) => void;

  /** Update cursor geo-position */
  setCursorPosition: (pos: { lat: number; lon: number } | null) => void;

  /** Mark globe as ready */
  setReady: (ready: boolean) => void;

  /** Switch tile mode */
  setTileMode: (mode: TileMode) => void;

  /** Switch base layer style */
  setBaseLayerStyle: (style: BaseLayerStyle) => void;

  /** Fly camera to coordinates */
  flyTo: (lon: number, lat: number, alt?: number, duration?: number, heading?: number, pitch?: number) => void;

  /** Reset to default globe view */
  resetView: () => void;

  /** Toggle air layer visibility */
  toggleAirLayer: () => void;

  /** Toggle aircraft labels visibility */
  toggleAirLabels: () => void;

  /** Toggle rail layer visibility */
  toggleRailLayer: () => void;

  /** Toggle rail labels visibility */
  toggleRailLabels: () => void;

  /** Toggle space layer visibility */
  toggleSpaceLayer: () => void;

  /** Toggle satellite labels visibility */
  toggleSpaceLabels: () => void;

  /** Toggle Starlink visibility */
  toggleStarlink: () => void;

  /** Toggle EW/Signal layer visibility */
  toggleEWLayer: () => void;

  /** Toggle zones layer visibility */
  toggleZonesLayer: () => void;

  /** Toggle sea/maritime layer visibility */
  toggleSeaLayer: () => void;

  /** Toggle vessel labels visibility */
  toggleSeaLabels: () => void;

  /** Toggle earthquake layer visibility */
  toggleQuakeLayer: () => void;

  /** Toggle global earthquakes (USGS) sub-toggle */
  toggleQuakeGlobal: () => void;

  /** Toggle Swiss earthquakes (SED) sub-toggle */
  toggleQuakeSwiss: () => void;

  /** Toggle wildfire layer visibility */
  toggleFireLayer: () => void;

  /** Toggle EONET natural events layer visibility */
  toggleEonetLayer: () => void;

  /** Set the active visual mode / shader preset */
  setVisualMode: (mode: VisualMode) => void;

  /** Update filter settings for a specific mode */
  updateFilterSettings: <M extends keyof FilterSettings>(
    mode: M,
    partial: Partial<FilterSettings[M]>,
  ) => void;
}

// ─── Default Camera (Europe focus) ──────────────────────────────

const DEFAULT_CAMERA: CameraPosition = {
  lat: 47.36339,  // Zürich
  lon: 8.54379,
  alt: 644,
  heading: 348.3,
  pitch: -20.5,
};

// ─── View Mode from Altitude ────────────────────────────────────

function viewModeFromAlt(altMeters: number): ViewMode {
  if (altMeters > 2_000_000) return 'globe';
  if (altMeters > 50_000) return 'region';
  return 'city';
}

// ─── Store ──────────────────────────────────────────────────────

export const useGlobeStore = create<GlobeState>((set, get) => ({
  viewer: null,
  cameraPosition: DEFAULT_CAMERA,
  selectedEntityId: null,
  trackedEntityId: null,
  viewMode: 'globe',
  cursorPosition: null,
  ready: false,
  tileMode: 'google3d',
  baseLayerStyle: 'standard',
  airLayerVisible: false,
  airLabelsVisible: true,
  railLayerVisible: false,
  railLabelsVisible: true,
  spaceLayerVisible: false,
  spaceLabelsVisible: true,
  starlinkVisible: false,
  ewLayerVisible: false,
  zonesLayerVisible: false,
  seaLayerVisible: false,
  seaLabelsVisible: true,
  quakeLayerVisible: false,
  quakeGlobalVisible: true,
  quakeSwissVisible: true,
  fireLayerVisible: false,
  eonetLayerVisible: false,
  visualMode: 'standard' as VisualMode,
  filterSettings: { ...DEFAULT_FILTER_SETTINGS },

  setViewer: (viewer) => set({ viewer }),

  updateCameraPosition: (pos) =>
    set({
      cameraPosition: pos,
      viewMode: viewModeFromAlt(pos.alt),
    }),

  setSelectedEntity: (id) => set({ selectedEntityId: id }),

  setTrackedEntity: (id) => set({ trackedEntityId: id }),

  setCursorPosition: (pos) => set({ cursorPosition: pos }),

  setReady: (ready) => set({ ready }),

  setTileMode: (mode) => set({ tileMode: mode }),

  setBaseLayerStyle: (style) => set({ baseLayerStyle: style }),

  flyTo: (lon, lat, alt = 1_500, duration = 2.0, heading, pitch) => {
    const { viewer } = get();
    if (!viewer) return;

    viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(lon, lat, alt),
      orientation: {
        heading: Cesium.Math.toRadians(heading ?? DEFAULT_CAMERA.heading),
        pitch: Cesium.Math.toRadians(pitch ?? DEFAULT_CAMERA.pitch),
        roll: 0,
      },
      duration,
    });
  },

  resetView: () => {
    const { viewer } = get();
    if (!viewer) return;

    viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(
        DEFAULT_CAMERA.lon,
        DEFAULT_CAMERA.lat,
        DEFAULT_CAMERA.alt,
      ),
      orientation: {
        heading: Cesium.Math.toRadians(DEFAULT_CAMERA.heading),
        pitch: Cesium.Math.toRadians(DEFAULT_CAMERA.pitch),
        roll: 0,
      },
      duration: 2.5,
    });
  },

  toggleAirLayer: () => set((state) => ({ airLayerVisible: !state.airLayerVisible })),

  toggleAirLabels: () => set((state) => ({ airLabelsVisible: !state.airLabelsVisible })),

  toggleRailLayer: () => set((state) => ({ railLayerVisible: !state.railLayerVisible })),

  toggleRailLabels: () => set((state) => ({ railLabelsVisible: !state.railLabelsVisible })),

  toggleSpaceLayer: () => set((state) => ({ spaceLayerVisible: !state.spaceLayerVisible })),

  toggleSpaceLabels: () => set((state) => ({ spaceLabelsVisible: !state.spaceLabelsVisible })),

  toggleStarlink: () => set((state) => ({ starlinkVisible: !state.starlinkVisible })),

  toggleEWLayer: () => set((state) => ({ ewLayerVisible: !state.ewLayerVisible })),

  toggleZonesLayer: () => set((state) => ({ zonesLayerVisible: !state.zonesLayerVisible })),

  toggleSeaLayer: () => set((state) => ({ seaLayerVisible: !state.seaLayerVisible })),

  toggleSeaLabels: () => set((state) => ({ seaLabelsVisible: !state.seaLabelsVisible })),

  toggleQuakeLayer: () => set((state) => ({ quakeLayerVisible: !state.quakeLayerVisible })),

  toggleQuakeGlobal: () => set((state) => ({ quakeGlobalVisible: !state.quakeGlobalVisible })),

  toggleQuakeSwiss: () => set((state) => ({ quakeSwissVisible: !state.quakeSwissVisible })),

  toggleFireLayer: () => set((state) => ({ fireLayerVisible: !state.fireLayerVisible })),

  toggleEonetLayer: () => set((state) => ({ eonetLayerVisible: !state.eonetLayerVisible })),

  setVisualMode: (mode) => set({ visualMode: mode }),

  updateFilterSettings: (mode, partial) =>
    set((state) => ({
      filterSettings: {
        ...state.filterSettings,
        [mode]: { ...state.filterSettings[mode], ...partial },
      },
    })),
}));
